import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import { formatDate } from '../utils/formatDate';
import MentorshipForm from './MentorshipForm';

function Mentorship({ juvenileId }) {
  const [assignments, setAssignments] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const { hasPermission } = useContext(AuthContext);

  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [assignmentsRes, usersRes] = await Promise.all([
        apiClient.get(`/api/mentorships/juvenile/${juvenileId}`),
        apiClient.get('/api/users')
      ]);
      setAssignments(assignmentsRes.data);
      setStaffUsers(usersRes.data);
    } catch (err) {
      setError('Failed to fetch mentorship data.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleFormSubmit = async (formData) => {
    try {
      if (editingAssignment) {
        await apiClient.put(`/api/mentorships/${editingAssignment.id}`, formData);
      } else {
        await apiClient.post('/api/mentorships', formData);
      }
      setShowAssignForm(false);
      setEditingAssignment(null);
      fetchAllData();
    } catch (err) {
      console.error('Failed to save mentorship:', err);
      alert('Failed to save mentorship assignment.');
    }
  };

  const activeAssignment = assignments.find(a => a.status === 'Active');
  const pastAssignments = assignments.filter(a => a.status !== 'Active');

  if (isLoading) return <div className="card"><p>Loading mentorship info...</p></div>;
  if (error) return <div className="card"><p className="text-red-500">{error}</p></div>;

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-slate-800 mb-0">Mentorship Program</h2>
        {hasPermission('manage-mentorships') && !activeAssignment && !showAssignForm && (
          <button onClick={() => setShowAssignForm(true)} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
            + Assign Mentor
          </button>
        )}
      </div>

      {showAssignForm && <MentorshipForm onSubmit={handleFormSubmit} onCancel={() => setShowAssignForm(false)} staffUsers={staffUsers} juvenileId={juvenileId} />}
      {editingAssignment && <MentorshipForm onSubmit={handleFormSubmit} initialData={editingAssignment} onCancel={() => setEditingAssignment(null)} staffUsers={staffUsers} juvenileId={juvenileId} />}

      {/* Active Assignment Section */}
      <div className="mt-4">
        <h3 className="text-lg font-semibold text-slate-700">Current Mentor</h3>
        {activeAssignment ? (
          <div className="p-4 border rounded-lg bg-slate-50">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold text-slate-800">{activeAssignment.mentor_name}</p>
                <p className="text-sm text-slate-500">Assigned on: {formatDate(activeAssignment.start_date)}</p>
              </div>
              {hasPermission('manage-mentorships') && !editingAssignment && (
                <button onClick={() => setEditingAssignment(activeAssignment)} className="text-sm text-slate-600 hover:text-gold">End Assignment</button>
              )}
            </div>
            {activeAssignment.notes && <p className="text-sm mt-2 pt-2 border-t">Notes: {activeAssignment.notes}</p>}
          </div>
        ) : (
          !showAssignForm && !editingAssignment && <p className="text-sm text-slate-500">No active mentor assigned.</p>
        )}
      </div>

      {/* Past Assignments Section */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-slate-700">Assignment History</h3>
        <div className="mt-2 border-t pt-2 space-y-2">
          {pastAssignments.length > 0 ? (
            pastAssignments.map(assignment => (
              <div key={assignment.id} className="p-3 rounded bg-slate-50">
                <p className="font-semibold text-slate-700">{assignment.mentor_name}</p>
                <p className="text-sm text-slate-500">
                  {formatDate(assignment.start_date)} - {formatDate(assignment.end_date)} ({assignment.status})
                </p>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">No past mentorship assignments.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Mentorship;